import React, { useState, useEffect, useRef } from 'react'
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom'
import { clientApi } from '../api/client'
import { userApi } from '../api/user'
import { urlUtils } from '../utils'
import { authStorage } from '../utils/authStorage'
import { ClientErrorView } from '../components/ClientErrorView'
import type { Client, ClientLoadErrorType, OtpVerificationData } from '../types'

type OtpErrorType = 'incomplete' | 'invalid' | 'resend'

const OTP_ERROR_CONFIG: Record<OtpErrorType, { variant: string; message: string }> = {
  incomplete: {
    variant: 'login-error-banner--server_error',
    message: 'Veuillez saisir les 4 chiffres du code',
  },
  invalid: {
    variant: 'login-error-banner--invalid_credentials',
    message: 'Code OTP invalide. Vérifiez les chiffres saisis ou demandez un nouveau code.',
  },
  resend: {
    variant: 'login-error-banner--server_error',
    message: 'Erreur lors du renvoi du code. Veuillez réessayer plus tard.',
  },
}

export const OtpVerificationView: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const clientId = searchParams.get('client_id')
  const isRedirectingRef = useRef(false)
  
  const [client, setClient] = useState<Client | null>(null)
  const [otpData, setOtpData] = useState<OtpVerificationData>({
    email: '',
    password: '',
    otp: 0
  })
  const [otpInputs, setOtpInputs] = useState<string[]>(['', '', '', ''])
  const [otpError, setOtpError] = useState<OtpErrorType | null>(null)
  const [clientLoadError, setClientLoadError] = useState<ClientLoadErrorType | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingClient, setIsLoadingClient] = useState(true)
  const [resendTimer, setResendTimer] = useState(0)
  const [stayConnected, setStayConnected] = useState(false)

  // Charger les informations du client et les credentials
  useEffect(() => {
    const loadData = async () => {
      if (isRedirectingRef.current) return

      if (!clientId) {
        setClientLoadError('missing_client_id')
        setIsLoadingClient(false)
        return
      }

      // Récupérer les credentials depuis le state de navigation
      const state = location.state as {
        email: string
        password: string
        stayConnected?: boolean
      } | null
      if (!state?.email || !state?.password) {
        setClientLoadError('missing_credentials')
        setIsLoadingClient(false)
        return
      }

      const stayConnectedFromState = state.stayConnected ?? false

      try {
        const clientData = await clientApi.getClientByPublicId(clientId)
        setClient(clientData)
        setStayConnected(stayConnectedFromState)
        setOtpData(prev => ({
          ...prev,
          email: state.email,
          password: state.password
        }))
      } catch (error) {
        console.error('Error loading client:', error)
        setClientLoadError('client_not_found')
      } finally {
        setIsLoadingClient(false)
      }
    }

    loadData()
  }, [clientId, location.state])

  // Timer pour le renvoi d'OTP
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [resendTimer])

  const handleOtpChange = (index: number, value: string) => {
    // Ne permettre que les chiffres
    if (!/^\d*$/.test(value)) return

    const newInputs = [...otpInputs]
    newInputs[index] = value
    setOtpInputs(newInputs)

    // Auto-focus sur le champ suivant
    if (value && index < otpInputs.length - 1) {
      const nextInput = document.getElementById(`otp-${index + 1}`)
      nextInput?.focus()
    }

    if (otpError) setOtpError(null)
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    // Supprimer et revenir au champ précédent
    if (e.key === 'Backspace' && !otpInputs[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`)
      prevInput?.focus()
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '')
    if (pastedData.length === 4) {
      const newInputs = pastedData.split('')
      setOtpInputs(newInputs)
      // Focus sur le dernier champ
      const lastInput = document.getElementById(`otp-${otpInputs.length - 1}`)
      lastInput?.focus()
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const otpString = otpInputs.join('')
    if (otpString.length !== 4) {
      setOtpError('incomplete')
      return
    }
    if (!clientId) return

    const verificationPayload: OtpVerificationData = {
      email: otpData.email,
      password: otpData.password,
      otp: parseInt(otpString, 10),
    }

    setOtpError(null)
    setIsLoading(true)
    try {
      const result = await userApi.verifyOtp(verificationPayload, clientId)
      
      if (result.token && client) {
        authStorage.persistTokenIfRequested(result.token, stayConnected)
        const redirectUrl = urlUtils.buildRedirectUrl(client.redirectUrl, {
          token: result.token,
          user_id: result.id?.toString() || '',
          email: result.email || ''
        })

        isRedirectingRef.current = true
        setOtpData({ email: '', password: '', otp: 0 })
        navigate(`/otp?client_id=${clientId}`, { replace: true, state: null })
        window.location.href = redirectUrl
      }
    } catch (error) {
      console.error('OTP verification error:', error)
      setOtpError('invalid')
      setOtpInputs(['', '', '', ''])
      // Focus sur le premier champ
      const firstInput = document.getElementById('otp-0')
      firstInput?.focus()
    } finally {
      setIsLoading(false)
    }
  }

  const handleResendOtp = async () => {
    if (resendTimer > 0) return

    try {
      // Renvoyer l'OTP en faisant une nouvelle tentative de connexion
      await userApi.login({ email: otpData.email, password: otpData.password }, clientId!)
      setResendTimer(60)
      setOtpError(null)
    } catch (error) {
      setOtpError('resend')
    }
  }

  if (isLoadingClient) {
    return (
      <div className="card fade-in">
        <div className="card-header">
          <div className="spinner"></div>
          <p>Chargement...</p>
        </div>
      </div>
    )
  }

  if (clientLoadError) {
    return <ClientErrorView type={clientLoadError} clientId={clientId} />
  }

  if (!client) {
    return null
  }

  return (
    <div className="card fade-in">
      <div className="card-header">
        {client.logo && (
          <img 
            src={client.logo} 
            alt={client.title} 
            style={{ height: '60px', marginBottom: '1rem' }}
          />
        )}
        <h1 className="card-title">Vérification d'email</h1>
        <p className="card-subtitle">
          Un code a été envoyé à<br />
          <strong>{otpData.email}</strong>
        </p>
      </div>

      {otpError && (
        <div
          className={`login-error-banner ${OTP_ERROR_CONFIG[otpError].variant}`}
          role="alert"
          aria-live="polite"
        >
          <p>{OTP_ERROR_CONFIG[otpError].message}</p>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label className="form-label">
            Code de vérification (4 chiffres)
          </label>
          <div 
            className={`otp-inputs${otpError ? ' shake' : ''}`}
            onPaste={handlePaste}
          >
            {otpInputs.map((value, index) => (
              <input
                key={index}
                id={`otp-${index}`}
                type="text"
                inputMode="numeric"
                maxLength={1}
                className="form-input otp-input"
                aria-invalid={otpError === 'invalid' || otpError === 'incomplete'}
                value={value}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                disabled={isLoading}
                autoComplete="off"
              />
            ))}
          </div>
        </div>

        <button
          type="submit"
          className="btn btn-primary btn-full"
          disabled={isLoading || otpInputs.some(input => !input)}
        >
          {isLoading ? (
            <>
              <div className="spinner"></div>
              Vérification...
            </>
          ) : (
            'Vérifier le code'
          )}
        </button>
      </form>

      <div className="card-footer">
        <p className="card-footer-text">
          Vous n'avez pas reçu le code ?{' '}
          {resendTimer > 0 ? (
            <span className="card-footer-text">
              Renvoyer dans {resendTimer}s
            </span>
          ) : (
            <button
              type="button"
              onClick={handleResendOtp}
              className="link"
              style={{ background: 'none', border: 'none', padding: 0 }}
            >
              Renvoyer le code
            </button>
          )}
        </p>
      </div>

      <div style={{ textAlign: 'center', marginTop: '1rem' }}>
        <a 
          href={`/login?client_id=${clientId}`}
          className="link"
          style={{ fontSize: '0.875rem' }}
        >
          ← Retour à la connexion
        </a>
      </div>
    </div>
  )
}
